/**
 * Small UI primitives shared across the customer portal pages.
 *
 * Ported from `screens/src/components/{PageHeader,StatusBadge}.jsx`. Lives in
 * `features/portal/` for now; once `libs/ui` ships these will move there and
 * this file becomes a thin re-export.
 */
import type { JSX, ReactNode } from 'react';

interface PageHeaderProps {
  readonly title: string;
  readonly description?: string;
  readonly actions?: ReactNode;
}

export function PageHeader({ title, description, actions }: PageHeaderProps): JSX.Element {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
        {description ? <p className="text-sm text-muted mt-1">{description}</p> : null}
      </div>
      {actions ? <div className="flex items-center gap-3">{actions}</div> : null}
    </div>
  );
}

const STATUS_COLORS: Readonly<Record<string, string>> = {
  ACTIVE:           'bg-success-light text-success',
  COMPLETED:        'bg-success-light text-success',
  DELIVERED:        'bg-success-light text-success',
  VERIFIED:         'bg-success-light text-success',
  RESOLVED:         'bg-success-light text-success',
  IN_TRANSIT:       'bg-info-light text-info',
  IN_PROGRESS:      'bg-info-light text-info',
  PROCESSING:       'bg-info-light text-info',
  SORTING:          'bg-info-light text-info',
  ACCEPTED:         'bg-info-light text-info',
  PENDING:          'bg-warning-light text-warning',
  OUT_FOR_DELIVERY: 'bg-warning-light text-warning',
  REGISTERED:       'bg-nipost-green-light text-nipost-green',
  FAILED:           'bg-danger-light text-danger',
  FAILED_DELIVERY:  'bg-danger-light text-danger',
  RETURNED:         'bg-gray-100 text-gray-600',
  LOST:             'bg-danger-light text-danger',
  DESTROYED:        'bg-gray-100 text-gray-600',
};

interface StatusBadgeProps {
  readonly status: string;
  readonly className?: string;
}

export function StatusBadge({ status, className = '' }: StatusBadgeProps): JSX.Element {
  const colors = STATUS_COLORS[status] ?? 'bg-gray-100 text-gray-600';
  const label = status.replace(/_/g, ' ');
  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colors} ${className}`}
    >
      {label}
    </span>
  );
}
